import { AlertCircle, CheckCircle2, Eye, FileText, History, Paperclip } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Event } from '@/types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const eventTypeIcons = {
  visit: Eye,
  progress: CheckCircle2,
  fault: AlertCircle,
};

const eventTypeColors = {
  visit: 'bg-primary text-primary-foreground',
  progress: 'bg-success text-success-foreground',
  fault: 'bg-destructive text-destructive-foreground',
};

const eventTypeLabels = {
  visit: 'ביקור',
  progress: 'שאלה',
  fault: 'תקלה',
};

interface ProjectTimelineProps {
  events: Event[];
  onSelectEvent: (event: Event) => void;
  onAddFirst: () => void;
  onFaultCompleted: (eventId: string) => void;
  onViewHistory: (event: Event) => void;
  groupTimelineByDay?: boolean;
}

export default function ProjectTimeline({ events, onSelectEvent, onAddFirst, onFaultCompleted, onViewHistory, groupTimelineByDay = false }: ProjectTimelineProps) {
  const renderEvent = (event: Event, index: number) => {
    const Icon = eventTypeIcons[event.type];
    const hasAttachments = event.files && event.files.length > 0;
    return (
      <div
        key={event.id}
        className="relative flex gap-4 pb-6 animate-slide-up cursor-pointer"
        style={{ animationDelay: `${index * 50}ms` }}
        onClick={() => onSelectEvent(event)}
      >
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10",
          eventTypeColors[event.type]
        )}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 bg-card rounded-xl p-4 border border-border shadow-soft hover:border-primary/50 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-secondary">
                {eventTypeLabels[event.type]} { event.status === "DRAFT" && "(טיוטה)" }
              </span>
              {hasAttachments && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                        <Paperclip className="w-3 h-3 text-accent-foreground" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>מכיל קבצים</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center hover:bg-accent transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewHistory(event);
                      }}
                    >
                      <History className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>צפייה בלוג שינויים</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <span className="text-xs text-muted-foreground">
              {format(new Date(event.date), 'd בMMMM yyyy', { locale: he })}
            </span>
          </div>
          <p className="text-sm leading-relaxed">{event.text}</p>
          <div className="flex justify-between mt-2">
            {event.tags.length > 0 && (
              <div className="flex gap-1 mt-3">
                {event.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-0.5 bg-accent text-accent-foreground rounded-md text-xs"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
            {event.type === 'fault' && !event.completed && (
              <button
                className="text-xs text-destructive font-medium flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onFaultCompleted(event.id);
                }}
              >
                <AlertCircle className="w-4 h-4" />
                סמן כטופל
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Group events by calendar day, preserving the incoming order (no re-sorting).
  const groupEventsByDay = () => {
    const groups: { key: string; label: string; events: Event[] }[] = [];
    const indexByKey = new Map<string, number>();
    for (const event of events) {
      const date = new Date(event.date);
      const key = format(date, 'yyyy-MM-dd');
      if (!indexByKey.has(key)) {
        indexByKey.set(key, groups.length);
        groups.push({
          key,
          label: format(date, 'd בMMMM yyyy', { locale: he }),
          events: [],
        });
      }
      groups[indexByKey.get(key)!].events.push(event);
    }
    return groups;
  };

  return (
    <div className="space-y-4">
      {events.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>אין תיעודים עדיין</p>
          <button
            onClick={onAddFirst}
            className="mt-4 text-primary font-medium"
          >
            הוסף תיעוד ראשון
          </button>
        </div>
      ) : groupTimelineByDay ? (
        (() => {
          // One global running index across all groups keeps animationDelay continuous.
          let runningIndex = 0;
          return (
            <div className="space-y-6">
              {groupEventsByDay().map((group) => (
                <div key={group.key}>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">{group.label}</h3>
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute top-0 bottom-0 right-[19px] w-0.5 bg-border" />
                    {group.events.map((event) => renderEvent(event, runningIndex++))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute top-0 bottom-0 right-[19px] w-0.5 bg-border" />

          {events.map((event, index) => renderEvent(event, index))}
        </div>
      )}
    </div>
  );
}
